import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Clock,
  Loader2,
  MoreHorizontal,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/api/client";
import type {
  CreateSlaPolicyRequest,
  MailboxResponse,
  SlaPolicyOut,
} from "@/api/schemas";
import {
  createSlaPolicy,
  deleteSlaPolicy,
  listSlaPolicies,
  patchSlaPolicy,
} from "@/api/sla-policies";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableBodySkeleton } from "@/components/ui/skeleton";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import { cn } from "@/lib/cn";

const SLA_QUERY = ["sla-policies"] as const;
const MAILBOXES_QUERY = ["mailboxes"] as const;

function SlaPoliciesAdminPage() {
  const { data: policies, isLoading } = useQuery({
    queryKey: SLA_QUERY,
    queryFn: listSlaPolicies,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SlaPolicyOut | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SlaPolicyOut | null>(null);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium tracking-tight">SLA policies</h1>
          <p className="mt-1 text-sm text-text-muted">
            Define expected response and resolution windows. Each new ticket is
            matched against these on arrival; the highest-priority match wins,
            falling back to the default policy.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Create policy
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-surface">
        {isLoading ? (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>First response</TH>
                <TH>Resolution</TH>
                <TH>Priority</TH>
                <TH>Default</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TableBodySkeleton rows={4} cells={6} />
          </Table>
        ) : !policies || policies.length === 0 ? (
          <EmptyState />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>First response</TH>
                <TH>Resolution</TH>
                <TH>Priority</TH>
                <TH>Default</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TBody>
              {policies.map((p) => (
                <TR key={p.id}>
                  <TD className="font-medium">{p.name}</TD>
                  <TD className="text-xs text-text-muted">
                    {formatMinutes(p.first_response_minutes)}
                  </TD>
                  <TD className="text-xs text-text-muted">
                    {formatMinutes(p.resolution_minutes)}
                  </TD>
                  <TD className="text-xs text-text-muted">{p.priority}</TD>
                  <TD>{p.is_default && <DefaultBadge />}</TD>
                  <TD>
                    <RowActionMenu
                      onEdit={() => setEditTarget(p)}
                      onDelete={() => setDeleteTarget(p)}
                      isDefault={p.is_default}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditDialog
        policy={editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
      />
      <DeleteDialog
        policy={deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-subtle">
        <Clock className="h-4 w-4 text-text-muted" />
      </div>
      <div className="text-sm font-medium">No policies yet</div>
      <div className="mt-1 text-xs text-text-muted">
        Click <span className="font-mono">Create policy</span> to add one.
        Every org needs at least a default policy.
      </div>
    </div>
  );
}

function DefaultBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
      default
    </span>
  );
}

function RowActionMenu({
  onEdit,
  onDelete,
  isDefault,
}: {
  onEdit: () => void;
  onDelete: () => void;
  isDefault: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          disabled={isDefault}
          onSelect={onDelete}
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type FormValues = {
  name: string;
  first_response_minutes: number;
  resolution_minutes: number;
  priority: number;
  is_default: boolean;
  vip_only: boolean;
  mailbox_ids: string[];
};

function defaultFormValues(): FormValues {
  return {
    name: "",
    first_response_minutes: 240,
    resolution_minutes: 1440,
    priority: 0,
    is_default: false,
    vip_only: false,
    mailbox_ids: [],
  };
}

function valuesFromPolicy(policy: SlaPolicyOut): FormValues {
  const conditions = (policy.conditions ?? {}) as {
    vip_only?: boolean;
    mailbox_ids?: string[];
  };
  return {
    name: policy.name,
    first_response_minutes: policy.first_response_minutes,
    resolution_minutes: policy.resolution_minutes,
    priority: policy.priority,
    is_default: policy.is_default,
    vip_only: conditions.vip_only ?? false,
    mailbox_ids: conditions.mailbox_ids ?? [],
  };
}

function buildPayload(v: FormValues): CreateSlaPolicyRequest {
  // Drop unset condition keys so the JSONB stays tight + the matcher's
  // "is this empty?" checks work cleanly.
  const conditions: Record<string, unknown> = {};
  if (v.vip_only) conditions.vip_only = true;
  if (v.mailbox_ids.length > 0) conditions.mailbox_ids = v.mailbox_ids;
  return {
    name: v.name.trim(),
    first_response_minutes: v.first_response_minutes,
    resolution_minutes: v.resolution_minutes,
    priority: v.priority,
    is_default: v.is_default,
    conditions: conditions as CreateSlaPolicyRequest["conditions"],
  };
}

function CreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<FormValues>({ defaultValues: defaultFormValues() });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => createSlaPolicy(buildPayload(v)),
    onSuccess: (created) => {
      toast.success(`Created ${created.name}`);
      void qc.invalidateQueries({ queryKey: SLA_QUERY });
      onOpenChange(false);
      form.reset(defaultFormValues());
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not create policy");
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) form.reset(defaultFormValues());
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create SLA policy</DialogTitle>
          <DialogDescription>
            Specify response and resolution windows. Conditions filter which
            tickets the policy applies to — tickets that match no conditions
            fall back to the default.
          </DialogDescription>
        </DialogHeader>
        <PolicyForm form={form} mutation={mutation} submitLabel="Create" />
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  policy,
  onOpenChange,
}: {
  policy: SlaPolicyOut | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<FormValues>({
    values: policy ? valuesFromPolicy(policy) : undefined,
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => {
      if (!policy) throw new Error("no policy");
      return patchSlaPolicy(policy.id, buildPayload(v));
    },
    onSuccess: (updated) => {
      toast.success(`Updated ${updated.name}`);
      void qc.invalidateQueries({ queryKey: SLA_QUERY });
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not update policy");
    },
  });

  if (!policy) return null;

  return (
    <Dialog open={!!policy} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit SLA policy</DialogTitle>
          <DialogDescription className="text-xs">
            {policy.name}
          </DialogDescription>
        </DialogHeader>
        <PolicyForm form={form} mutation={mutation} submitLabel="Save" />
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  policy,
  onOpenChange,
}: {
  policy: SlaPolicyOut | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => {
      if (!policy) throw new Error("no policy");
      return deleteSlaPolicy(policy.id);
    },
    onSuccess: () => {
      toast.success(`Deleted ${policy?.name ?? "policy"}`);
      void qc.invalidateQueries({ queryKey: SLA_QUERY });
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not delete policy");
    },
  });

  if (!policy) return null;
  const blockedDefault = policy.is_default;

  return (
    <Dialog open={!!policy} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete policy</DialogTitle>
          <DialogDescription>
            {blockedDefault ? (
              <>
                <strong>{policy.name}</strong> is the default policy and cannot
                be deleted. Promote another policy to default first.
              </>
            ) : (
              <>
                Delete <strong>{policy.name}</strong>? Existing tickets keep
                their due-dates; only future tickets stop matching.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || blockedDefault}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PolicyForm({
  form,
  mutation,
  submitLabel,
}: {
  form: ReturnType<typeof useForm<FormValues>>;
  mutation: { mutate: (v: FormValues) => void; isPending: boolean };
  submitLabel: string;
}) {
  const { data: mailboxes } = useQuery({
    queryKey: MAILBOXES_QUERY,
    queryFn: () => apiFetch<MailboxResponse[]>("/api/mailboxes"),
  });

  return (
    <form
      onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
      className="flex flex-col gap-3"
    >
      <FormField label="Name">
        <Input
          {...form.register("name", { required: true, maxLength: 120 })}
          placeholder="VIP within 1 hour"
          autoFocus
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="First response (min)">
          <Input
            type="number"
            min={1}
            max={10080}
            {...form.register("first_response_minutes", {
              required: true,
              valueAsNumber: true,
              min: 1,
              max: 10080,
            })}
          />
          <MinutesHint
            value={form.watch("first_response_minutes")}
            id="fr-hint"
          />
        </FormField>
        <FormField label="Resolution (min)">
          <Input
            type="number"
            min={1}
            max={10080}
            {...form.register("resolution_minutes", {
              required: true,
              valueAsNumber: true,
              min: 1,
              max: 10080,
            })}
          />
          <MinutesHint
            value={form.watch("resolution_minutes")}
            id="res-hint"
          />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Priority">
          <Input
            type="number"
            min={0}
            max={10000}
            {...form.register("priority", {
              valueAsNumber: true,
              min: 0,
              max: 10000,
            })}
          />
        </FormField>
        <FormField label="Default">
          <label className="flex h-9 items-center gap-2 rounded border border-border bg-surface px-3 text-sm">
            <input
              type="checkbox"
              {...form.register("is_default")}
              className="h-4 w-4"
            />
            <span>Make this the org default</span>
          </label>
        </FormField>
      </div>
      <fieldset className="rounded border border-border p-3">
        <legend className="px-1 text-xs font-medium text-text-muted">
          Conditions
        </legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            {...form.register("vip_only")}
            className="h-4 w-4"
          />
          <span>Only apply to VIP contacts</span>
        </label>
        <div className="mt-3">
          <Label className="mb-1 block">Restrict to mailboxes</Label>
          {mailboxes && mailboxes.length > 0 ? (
            <div className="flex flex-col gap-1">
              {mailboxes.map((m) => (
                <MailboxToggle
                  key={m.id}
                  mailbox={m}
                  selected={form.watch("mailbox_ids").includes(m.id)}
                  onToggle={(checked) => {
                    const cur = form.getValues("mailbox_ids");
                    form.setValue(
                      "mailbox_ids",
                      checked
                        ? [...cur, m.id]
                        : cur.filter((id) => id !== m.id),
                      { shouldDirty: true },
                    );
                  }}
                />
              ))}
              <p className="mt-1 text-xs text-text-muted">
                Leave all unchecked to apply to every mailbox.
              </p>
            </div>
          ) : (
            <p className="text-xs text-text-muted">
              No mailboxes configured.
            </p>
          )}
        </div>
        <p className="mt-3 text-xs text-text-muted">
          Tag-based conditions will be supported once SLA re-evaluation on tag
          change ships — agents apply tags after a ticket arrives, so a
          tag-only rule wouldn't fire on creation.
        </p>
      </fieldset>
      <DialogFooter>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

function MailboxToggle({
  mailbox,
  selected,
  onToggle,
}: {
  mailbox: MailboxResponse;
  selected: boolean;
  onToggle: (checked: boolean) => void;
}) {
  return (
    <label
      className={cn(
        "flex items-center gap-2 rounded px-2 py-1 text-sm",
        "hover:bg-surface-subtle",
      )}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={(e) => onToggle(e.target.checked)}
        className="h-4 w-4"
      />
      <span className="font-mono text-xs">{mailbox.email_address}</span>
    </label>
  );
}

function MinutesHint({ value, id }: { value: number | undefined; id: string }) {
  if (!Number.isFinite(value)) return null;
  return (
    <p id={id} className="text-xs text-text-muted">
      {formatMinutes(value!)}
    </p>
  );
}

function FormField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function formatMinutes(min: number): string {
  if (!Number.isFinite(min) || min < 0) return "—";
  if (min < 60) return `${min}m`;
  if (min < 60 * 24) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  }
  const d = Math.floor(min / (60 * 24));
  const remH = Math.floor((min % (60 * 24)) / 60);
  return remH === 0 ? `${d}d` : `${d}d ${remH}h`;
}

export const Route = createFileRoute("/_auth/settings/sla-policies")({
  component: SlaPoliciesAdminPage,
});
